import MuiFormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MuiFormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, {
  type SelectProps as MuiSelectProps,
  SelectChangeEvent,
} from '@mui/material/Select';
import { forwardRef, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useFieldContext } from '~/common/form/formContexts';

type SelectProps = Omit<
  MuiSelectProps<string>,
  'onChange' | 'value' | 'variant'
> & {
  isDisabled?: boolean;
  isRequired?: boolean;
  useReactHooksForm?: boolean;
  useTanstackForm?: boolean;
  name?: string;
  options?: { label: string; value: string }[];
  errorMessage?: string;
  placeholder?: string;
  value?: string;
  variant?: 'outlined';
  rules?: {
    validate: (value: any) => string | null;
  };
  onChange?: (value: string) => void;
};

const Select = forwardRef<any, SelectProps>(
  ({ useReactHooksForm, useTanstackForm, ...props }, ref) => {
    if (useReactHooksForm) {
      return <HookFormSelect ref={ref} {...props} />;
    }
    if (useTanstackForm) {
      return <TanstackFormSelect ref={ref} {...props} />;
    }
    return <BaseSelect {...props} ref={ref} />;
  },
);

const HookFormSelect = forwardRef<any, SelectProps>(
  ({ name, rules, ...props }, ref) => {
    const {
      control,
      formState: { errors },
    } = useFormContext();
    return (
      <Controller
        name={name}
        rules={rules}
        control={control}
        render={({ onChange, value, ...hooksProps }, { invalid }) => (
          <BaseSelect
            {...props}
            {...hooksProps}
            ref={ref}
            onChange={(e) => {
              onChange(e);
            }}
            value={value}
            errorMessage={errors[name]?.message}
            error={invalid}
          />
        )}
      />
    );
  },
);

const BaseSelect = forwardRef<any, SelectProps>(
  (
    {
      children,
      required,
      id,
      name,
      label,
      isRequired,
      disabled,
      isDisabled,
      placeholder,
      options,
      value,
      errorMessage,
      onChange: onChangeProp,
      sx,
      inputProps: inputPropsProp,
      ...props
    },
    ref,
  ) => {
    const [localValue, setLocalValue] = useState('');

    useEffect(() => {
      setLocalValue(value ?? '');
    }, [value]);

    const onChange = (event: SelectChangeEvent<typeof value>) => {
      const rawValue = event.target.value;
      setLocalValue(rawValue);
      onChangeProp?.(rawValue);
    };

    const inputId = id ?? name;
    const labelId = inputId ? `${inputId}-label` : undefined;

    return (
      <MuiFormControl sx={sx} fullWidth error={props.error}>
        {label !== undefined && (
          <MuiFormLabel id={labelId} disabled={disabled || isDisabled}>
            {label}
          </MuiFormLabel>
        )}
        <MuiSelect
          ref={ref}
          required={isRequired ?? required ?? false}
          disabled={disabled || isDisabled}
          name={name}
          inputProps={inputPropsProp}
          value={localValue}
          onChange={onChange}
          {...props}
          labelId={labelId}
          SelectDisplayProps={{ id: inputId }}
          displayEmpty
          sx={{ width: '100%' }}
        >
          {placeholder !== undefined && (
            <MenuItem value="">{placeholder}</MenuItem>
          )}
          {options?.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
          {children}
        </MuiSelect>
        {props.error && errorMessage && (
          <FormHelperText>{errorMessage}</FormHelperText>
        )}
      </MuiFormControl>
    );
  },
);

const TanstackFormSelect = forwardRef<any, SelectProps>(
  ({ isDisabled, isRequired, ...props }, ref) => {
    const field = useFieldContext<string>();
    const errorMessage = field.state.meta.errors[0] as string | undefined;
    const hasError = field.state.meta.errors.length > 0;
    return (
      <BaseSelect
        {...props}
        ref={ref}
        value={field.state.value ?? ''}
        onChange={(value) => field.handleChange(value)}
        error={hasError}
        errorMessage={errorMessage}
        isDisabled={isDisabled}
        isRequired={isRequired}
      />
    );
  },
);

Select.displayName = 'Select';

export default Select;
